import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { BadgeDollarSign, Download } from "lucide-react";
import { formatCents } from "@shared/money";
import { errorMessage } from "@web/lib/api";
import { fmtDate } from "@web/lib/format";
import { commissionApi, commissionEntriesQuery, commissionPayoutUrl, commissionRulesQuery, type CommissionEntry } from "@web/lib/queries-commissions";
import { membersQuery } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { AdminsOnly } from "./Settings";
import {
  BottomSheet, Button, Card, EmptyState, Inline, Page, Picker, Region, Row, RowGroup,
  SegmentedControl, Skeleton, Stack, Stat, StatRow, StatusChip, Text, Workspace, useToast,
} from "@web/ui";

/** Commissions (SPEC §6.1 item 12, §7.13). Admins see everybody and can mark
 * entries paid; everybody else sees their own and nothing else — which is why the
 * member filter only appears for an admin. */
const STATUSES = [
  { key: "payable", label: "Payable" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
] as const;
type Status = (typeof STATUSES)[number]["key"];
export type CommissionsSearch = { status?: Status; member?: string };

/** The shareable address contract: unknown keys and oversized values are dropped,
 * and an absent key means the screen's own default. */
export function parseCommissionsSearch(raw: Record<string, unknown>): CommissionsSearch {
  const out: CommissionsSearch = {};
  if (STATUSES.some((s) => s.key === raw.status)) out.status = raw.status as Status;
  if (typeof raw.member === "string" && raw.member && raw.member.length <= 200) out.member = raw.member;
  return out;
}

const TONES = { payable: "warn", paid: "success", pending: "neutral", void: "neutral" } as const;
const tone = (status: string) => TONES[status as keyof typeof TONES] ?? "neutral";

export function Commissions() {
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  // Status and member live in the URL, so Back and a shared link both show the
  // entries the person was looking at (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/more/commissions" });
  const status = search.status ?? "payable";
  const memberId = search.member ?? "";
  const setSearch = (next: CommissionsSearch) => navigate({ to: "/more/commissions", search: { ...search, ...next }, replace: true });
  const [selected, setSelected] = useState<string[]>([]);
  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);

  const members = useQuery({ ...membersQuery, enabled: me.companyAdmin });
  const query = { ...(status === "all" ? {} : { status }), ...(memberId ? { memberId } : {}) };
  const entries = useQuery(commissionEntriesQuery(query));
  const rows = entries.data ?? [];

  const totals = useMemo(() => ({
    payable: rows.filter(row => row.status === "payable").reduce((sum, row) => sum + row.amount, 0),
    paid: rows.filter(row => row.status === "paid").reduce((sum, row) => sum + row.amount, 0),
    selected: rows.filter(row => selected.includes(row.id)).reduce((sum, row) => sum + row.amount, 0),
  }), [rows, selected]);

  const memberNames = useMemo(() => new Map((members.data ?? []).map(member => [member.id, member.name ?? member.email])), [members.data]);
  const byMember = useMemo(() => {
    const groups = new Map<string, { name: string; rows: CommissionEntry[] }>();
    for (const row of rows) {
      const key = row.memberId ?? "unassigned";
      const group = groups.get(key) ?? { name: memberNames.get(row.memberId ?? "") ?? "Unassigned", rows: [] };
      group.rows.push(row);
      groups.set(key, group);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, memberNames]);

  const payable = rows.filter(row => row.status === "payable");
  const toggle = (id: string) => setSelected(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);

  async function markPaid() {
    setBusy(true);
    try {
      const result = await commissionApi.pay(selected);
      toast.success(`${result.paid} ${result.paid === 1 ? "entry" : "entries"} marked paid`);
      setSelected([]);
      setPaying(false);
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  return <Page>
    <Stack gap={4}>
      <Inline justify="between" align="start" wrap>
        <Stack gap={1}>
          <Text as="h2" variant="headline">{me.companyAdmin ? "Commissions" : "Your commissions"}</Text>
          <Text variant="caption" tone="secondary">
            {me.companyAdmin
              ? "One entry per rule per payment. Refunds appear as negative entries."
              : "Your entries only. Ask an admin about anything that looks wrong."}
          </Text>
        </Stack>
        {me.companyAdmin && <Button variant="secondary" fullWidth={false} icon={<Download aria-hidden size={18} />} onClick={() => window.open(commissionPayoutUrl(status === "all" ? undefined : status), "_blank")}>
          Payout CSV
        </Button>}
      </Inline>

      <StatRow>
        <Stat label="Payable" cents={totals.payable} />
        <Stat label="Paid" cents={totals.paid} />
        {selected.length > 0 && <Stat label={`${selected.length} selected`} cents={totals.selected} />}
      </StatRow>

      <Inline gap={2} wrap>
        <SegmentedControl items={STATUSES} value={status} onChange={(value) => setSearch({ status: value })} />
        {me.companyAdmin && <Picker label="Member" value={memberId} onChange={(value) => setSearch({ member: value || undefined })}
          options={[{ value: "", label: "Everybody" }, ...(members.data ?? []).map(member => ({ value: member.id, label: member.name ?? member.email }))]} />}
      </Inline>

      {entries.isError
        ? <EmptyState icon={BadgeDollarSign} title="Commissions could not be loaded" body={errorMessage(entries.error)}
            action={<Button onClick={() => void entries.refetch()}>Try again</Button>} />
        : !entries.data ? <Skeleton variant="cards" count={2} />
          : rows.length === 0
            ? <EmptyState icon={BadgeDollarSign} title={status === "paid" ? "Nothing paid yet" : "Nothing payable"} body="Entries appear here as payments come in." />
            : <Workspace variant="overview">
              {byMember.map((group, index) => <Region key={group.name} area={index % 2 === 0 ? "main" : "aside"} label={group.name}>
                <Stack gap={2}>
                  <Inline justify="between">
                    <Text as="h3" variant="title">{group.name}</Text>
                    <StatusChip tone="neutral">{formatCents(group.rows.reduce((sum, row) => sum + row.amount, 0))}</StatusChip>
                  </Inline>
                  <RowGroup>
                    {group.rows.map(row => <Row key={row.id}
                      title={`${formatCents(row.amount)} · ${row.ratePct}% of ${formatCents(row.basisAmount)}`}
                      subtitle={`${row.eventName || (row.eventDate ? fmtDate(row.eventDate) : "Booking")}${row.paidAt ? ` · paid ${fmtDate(row.paidAt.slice(0, 10))}` : ""}${row.notes ? ` · ${row.notes}` : ""}`}
                      wrap
                      selected={selected.includes(row.id)}
                      trailing={<StatusChip tone={tone(row.status)}>{row.status}</StatusChip>}
                      onClick={me.companyAdmin && row.status === "payable" ? () => toggle(row.id) : undefined} />)}
                  </RowGroup>
                </Stack>
              </Region>)}
            </Workspace>}

      {me.companyAdmin && payable.length > 0 && <Inline gap={2} wrap>
        <Button fullWidth={false} disabled={selected.length === 0} onClick={() => setPaying(true)}>
          Mark {selected.length || ""} paid
        </Button>
        <Button variant="secondary" fullWidth={false} onClick={() => setSelected(payable.map(row => row.id))}>Select all payable</Button>
        {selected.length > 0 && <Button variant="secondary" fullWidth={false} onClick={() => setSelected([])}>Clear</Button>}
      </Inline>}
    </Stack>

    <BottomSheet open={paying} onClose={() => setPaying(false)} title="Mark these paid?"
      footer={<Button loading={busy} onClick={() => void markPaid()}>Mark them paid</Button>}>
      <Stack gap={3}>
        <Text>{selected.length} {selected.length === 1 ? "entry" : "entries"}, {formatCents(totals.selected)} in total.</Text>
        <Text tone="secondary">This records that the money has left. It does not move any money itself — pay them however you normally do, then mark them here.</Text>
      </Stack>
    </BottomSheet>
  </Page>;
}

/** Settings › Payments (SPEC §6.1 item 13). Deposit, balance and fee defaults,
 * plus the commission rules that decide who earns what. The defaults themselves
 * live on Settings › Company; this screen is about the money rules. */
export function SettingsPayments() {
  const me = useMe();
  const rules = useQuery({ ...commissionRulesQuery, enabled: me.companyAdmin });
  if (!me.companyAdmin) return <AdminsOnly />;

  return <Page>
    <Workspace variant="form"><Region area="main"><Stack gap={4}>
      <Card><Stack gap={2}>
        <Text as="h2" variant="title">Who earns commission</Text>
        <Text variant="caption" tone="secondary">
          On every payment that is not a damage deposit, one entry per rule that matches the booking's booker or venue. A refund produces a negative entry.
        </Text>
        {rules.isError
          ? <EmptyState icon={BadgeDollarSign} title="Rules could not be loaded" body={errorMessage(rules.error)} action={<Button onClick={() => void rules.refetch()}>Try again</Button>} />
          : !rules.data ? <Skeleton count={3} />
            : rules.data.length === 0
              ? <EmptyState icon={BadgeDollarSign} title="No commission rules" body="Add one per member who earns on bookings." />
              : <RowGroup>
                {rules.data.map(rule => <Row key={rule.id}
                  title={`${rule.ratePct}% of ${rule.on === "subtotal" ? "the subtotal" : "the total"}`}
                  subtitle={`${rule.basis === "venue_all" ? "Every booking at the venue" : "Bookings they booked"}${rule.effectiveFrom ? ` · from ${rule.effectiveFrom}` : ""}`}
                  wrap
                  trailing={<StatusChip tone={rule.active ? "success" : "neutral"}>{rule.active ? "Active" : "Off"}</StatusChip>} />)}
              </RowGroup>}
        <Text variant="caption" tone="tertiary">Rules are edited per member under Settings › Users.</Text>
      </Stack></Card>

      <Card><Stack gap={2}>
        <Text as="h2" variant="title">What clients can pay with</Text>
        <RowGroup>
          <Row title="Card" subtitle="Including Apple Pay and Google Pay, with the card fee added" wrap />
          <Row title="Bank transfer" subtitle="No fee; the date stays held while it clears" wrap />
        </RowGroup>
        <Text variant="caption" tone="tertiary">Deposit percentage, balance timing and the card fee are on Settings › Company.</Text>
      </Stack></Card>
    </Stack></Region></Workspace>
  </Page>;
}
